/**
 * Which organization the Shell should scope per-organization CMMD reads to.
 *
 * The Shell is cross-workspace and signs in with the user's CMMD identity, so
 * a proxied read that names no organization lands in the user's default one,
 * which is their personal space. The cloud project catalog already says where
 * the user's Environment runs. This resolver reads it and answers only when
 * the answer is unambiguous: one organization across the catalog's
 * Environments, or failing that one across its project rows. Several distinct
 * organizations mean the single-workspace rule does not apply, and the caller
 * leaves CMMD on its default rather than guessing (FOR-36406).
 *
 * @module usage/workspaceOrganization
 */
import type { CmmdForgeRuntimeListSnapshot } from "../cmmd/cmmdForgeRuntimeListTypes";

export type WorkspaceOrganizationSource = Pick<
  CmmdForgeRuntimeListSnapshot,
  "rows" | "environments"
>;

function soleOrganization(ids: readonly number[]): number | undefined {
  const distinct = new Set(ids);
  if (distinct.size !== 1) return undefined;
  const [only] = distinct;
  return only;
}

export function resolveWorkspaceOrganizationId(
  catalog: WorkspaceOrganizationSource,
): number | undefined {
  // Environments are the direct answer. When the catalog lists any, they
  // decide, and an ambiguous list is not rescued by the project rows.
  if (catalog.environments.length > 0) {
    return soleOrganization(catalog.environments.map((row) => row.cmmdOrganizationId));
  }
  return soleOrganization(catalog.rows.map((row) => row.cmmdOrganizationId));
}
